import {
  Box,
  Heading,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { AddToSlackButton } from './AddToSlackButton';
import { DeactivateBorrowerSlackInstallationsConfirmation } from './DeactivateBorrowerSlackInstallationsConfirmation';
import { SlackInstallationsContainer } from './SlackInstallationsContainer';
import { useGetActiveSlackInstallationsQuery } from 'borrower/domains/notifications/requests/slack.gql.generated';
import { FiSettings } from 'react-icons/fi';

export const SlackInstallationsView = ({
  borrowerId,
}: {
  borrowerId: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    data: slackInstallationsData,
    loading: slackInstallationsLoading,
    refetch: refetchSlackInstallationsData,
  } = useGetActiveSlackInstallationsQuery();

  const { data: userData, loading: userLoading } = useGetUserQuery();

  if (
    slackInstallationsLoading ||
    !slackInstallationsData ||
    !slackInstallationsData.activeSlackInstallations ||
    userLoading ||
    !userData ||
    !userData.user
  ) {
    return (
      <Skeleton data-testid="loading-slack-installations" height="600px" />
    );
  }

  return (
    <Box mt="35px" mb="70px">
      <Heading as="h4" size="md" textAlign="left" marginBottom="30px">
        Integrate with Slack
      </Heading>
      <Text width="40%" mt="10px" fontSize="sm" align="left">
        You can integrate Finley with your company&apos;s Slack workspace to
        receive notifications about credit facilities managed by Finley.
      </Text>
      <Text width="40%" mt="10px" fontSize="sm" align="left">
        There is no limit to the number of Slack channels where you can receive
        Finley notifications. For each channel you want to integrate, click on
        the button below and complete the prompts that follow.
      </Text>
      <Text width="40%" mt="10px" fontSize="sm" align="left">
        After integrating Finley with a Slack channel, you must invite Finley to
        the channel in order to finish the set-up process.{' '}
        <strong>
          You can do this by navigating to the channel in Slack and typing{' '}
        </strong>
        <code>/invite @Finley</code>
      </Text>
      <Box height="15px" />
      <AddToSlackButton borrowerId={borrowerId} />
      <Box height="60px" />
      <Flex alignItems="center" mb="35px">
        <Heading as="h4" size="md" textAlign="left">
          Integrated Slack channels
        </Heading>
        <Menu>
          <MenuButton
            as={IconButton}
            bg="transparent"
            icon={<Icon as={FiSettings} />}
            colorscheme="ghost"
          />
          <MenuList>
            <MenuItem
              onClick={() => {
                refetchSlackInstallationsData();
              }}
            >
              Refresh integrated channels
            </MenuItem>
            <MenuItem onClick={onOpen}>
              Deactivate all integrated channels
            </MenuItem>
          </MenuList>
        </Menu>
        <DeactivateBorrowerSlackInstallationsConfirmation
          onClose={onClose}
          isOpen={isOpen}
        />
      </Flex>
      <SlackInstallationsContainer
        slackInstallations={slackInstallationsData.activeSlackInstallations}
        isLoading={slackInstallationsLoading}
        organizationId={userData.user.organizationId}
      />
    </Box>
  );
};
